'use strict';
/*
  linger — the jorgen.larsen.id look. "Lines that linger." Near-black, warm
  off-white type, scarlet accent, sparse hand-drawn red/white doodles, a faint
  red halftone + film grain. Emphasised words get a VARIED hand-drawn mark
  (circle, underline, box, bracket, zig, marker swipe) — a different sketch each
  time, drawn on as the word appears and wobbling slightly so it reads as ink,
  not graphic. Calmer and more poetic than UTYPISK; in talk mode it's pure
  caption focus (his quote-reel look).
*/
const { layoutCue } = require('./_common');
const { FONTS } = require('../lib/draw');

const palette = { bg: '#0a0a0a', fg: '#f4f1ea', accent: '#e8211f', ink: '#0a0a0a' };

// ---- sparse hand-drawn doodles (unit space) ----
const R = (rnd, a, b) => a + rnd() * (b - a);
function gSquiggle(rnd){const p=[],n=28,w=R(rnd,3,5);for(let i=0;i<=n;i++){const f=i/n;p.push([f*2-1,Math.sin(f*Math.PI*w)*.5]);}return[p];}
function gStar(){const p=[],n=5;for(let i=0;i<=n*2;i++){const a=i/(n*2)*Math.PI*2-Math.PI/2,r=i%2?.42:1;p.push([Math.cos(a)*r,Math.sin(a)*r]);}return[p];}
function gBolt(){return[[[-.2,-1],[.25,-.2],[-.15,-.05],[.3,1],[.05,.15],[-.25,0]]];}
function gArrow(){return[[[-1,0],[1,0]],[[.5,-.4],[1,0],[.5,.4]]];}
function gCircle(rnd){const p=[],n=24,loops=R(rnd,1,1.3),wob=R(rnd,.06,.16);for(let i=0;i<=n;i++){const a=i/n*Math.PI*2*loops,r=1+Math.sin(a*R(rnd,2,4))*wob;p.push([Math.cos(a)*r,Math.sin(a)*r]);}return[p];}
const GENS = [gSquiggle, gStar, gBolt, gArrow, gCircle, gSquiggle, gStar];

// ---- per-word emphasis marks (px space, word centred at origin) ----
// stable hash → same word always gets the same kind of mark; a seeded RNG keeps
// each mark's wobble deterministic so it doesn't flicker frame-to-frame.
function hash(s){let h=2166136261;for(let i=0;i<s.length;i++){h^=s.charCodeAt(i);h=Math.imul(h,16777619);}return h>>>0;}
function seeded(s){let a=s>>>0;return()=>{a=(a+0x6D2B79F5)|0;let t=Math.imul(a^(a>>>15),1|a);t=(t+Math.imul(t^(t>>>7),61|t))^t;return((t^(t>>>14))>>>0)/4294967296;};}

function mEllipse(w2,h2,px,rng){const p=[],n=48,loops=1.08+rng()*0.22,off=(rng()-.5)*0.5;for(let i=0;i<=n;i++){const a=i/n*Math.PI*2*loops-Math.PI*0.5+off,wob=1+Math.sin(a*3+rng()*6)*0.05;p.push({x:Math.cos(a)*(w2+px*0.10)*wob,y:Math.sin(a)*(h2*1.12)*wob-h2*0.05});}return[p];}
function mUnderline(w2,h2,px,rng){const out=[],lines=rng()<.45?2:1;for(let s=0;s<lines;s++){const p=[],n=22,base=h2*0.96+s*px*0.11,amp=px*0.04*(1+rng());for(let i=0;i<=n;i++){const f=i/n;p.push({x:(f*2-1)*(w2+px*0.02),y:base+Math.sin(f*Math.PI*(1.5+rng()*1.5))*amp});}out.push(p);}return out;}
function mZig(w2,h2,px){const p=[],n=13,base=h2*0.98,amp=px*0.07;for(let i=0;i<=n;i++){const f=i/n;p.push({x:(f*2-1)*(w2+px*0.02),y:base+(i%2?amp:-amp)});}return[p];}
function mBox(w2,h2,px,rng){const o=()=>(rng()-.5)*px*0.12,L=-w2-px*0.07,Rr=w2+px*0.07,T=-h2*1.06,B=h2*1.0;return[[{x:L+o(),y:T+o()},{x:Rr+o(),y:T+o()},{x:Rr+o(),y:B+o()},{x:L+o(),y:B+o()},{x:L+o(),y:T+o()},{x:-w2*0.3,y:T-px*0.03+o()}]];}
function mBracket(w2,h2,px){const T=-h2*1.05,B=h2,e=px*0.16,L=-w2-px*0.06,Rr=w2+px*0.06;return[[{x:L+e,y:T},{x:L,y:T},{x:L,y:B},{x:L+e,y:B}],[{x:Rr-e,y:T},{x:Rr,y:T},{x:Rr,y:B},{x:Rr-e,y:B}]];}
// ---- funkier variants (still no circles) ----
function mDoubleUnder(w2,h2,px,rng){const out=[],lines=2;for(let s=0;s<lines;s++){const p=[],n=20,base=h2*0.95+s*px*(0.13+rng()*0.06),amp=px*0.05*(0.6+rng());for(let i=0;i<=n;i++){const f=i/n;p.push({x:(f*2-1)*(w2+px*0.04*rng()),y:base+Math.sin(f*Math.PI*(1.2+rng()*2))*amp});}out.push(p);}return out;}
function mScribble(w2,h2,px,rng){const p=[],n=32,base=h2*0.99,amp=px*0.10;for(let i=0;i<=n;i++){const f=i/n;p.push({x:(f*2-1)*(w2+px*0.03),y:base+(i%2?amp:-amp)*(0.65+rng()*0.7)});}return[p];}
function mCorners(w2,h2,px){const L=-w2-px*0.06,Rr=w2+px*0.06,T=-h2*1.05,B=h2*1.0,e=px*0.18;return[[{x:L+e,y:T},{x:L,y:T},{x:L,y:T+e}],[{x:Rr-e,y:T},{x:Rr,y:T},{x:Rr,y:T+e}],[{x:L,y:B-e},{x:L,y:B},{x:L+e,y:B}],[{x:Rr,y:B-e},{x:Rr,y:B},{x:Rr-e,y:B}]];}
function mStrike(w2,h2,px,rng){const p=[],n=18,base=-h2*0.08+(rng()-.5)*px*0.05,amp=px*0.03;for(let i=0;i<=n;i++){const f=i/n;p.push({x:(f*2-1)*(w2+px*0.09),y:base+Math.sin(f*Math.PI*2.2)*amp});}return[p];}
function mSideBar(w2,h2,px,rng){const x=(rng()<.5?-1:1)*(w2+px*0.10),T=-h2*0.95,B=h2*0.95,o=()=>(rng()-.5)*px*0.06;return[[{x:x+o(),y:T},{x:x+o(),y:B}]];}
// No circles — underline, brackets [ ], box and funky kin. Underline/bracket favoured;
// scribble, strike, double-underline, corner-ticks and a side-bar add unpredictability.
// mEllipse/mZig stay defined above but out of rotation. (+1 in the selector = marker swipe.)
const MARKS = [mUnderline, mBracket, mBox, mScribble, mDoubleUnder, mStrike, mCorners, mUnderline, mBracket, mSideBar];

function sketch(ctx, strokes, rev, lw, col, seed, st){
  ctx.lineCap='round'; ctx.lineJoin='round'; ctx.strokeStyle=col; ctx.lineWidth=lw;
  let total=0; strokes.forEach(s=>total+=s.length); let budget=Math.max(2,Math.floor(rev*total));
  for(const s of strokes){ if(budget<=1)break; const m=Math.min(s.length,budget); budget-=s.length;
    ctx.beginPath();
    for(let i=0;i<m;i++){ const a=s[i], jx=Math.sin(st.NOW/200+seed+i*0.8)*lw*0.32, jy=Math.cos(st.NOW/240+seed*1.7+i*0.6)*lw*0.32; i?ctx.lineTo(a.x+jx,a.y+jy):ctx.moveTo(a.x+jx,a.y+jy); }
    ctx.stroke();
  }
}
// thick marker swipe BEHIND the word (uneven, slightly tilted), grows on with rev
function swipe(ctx, w2, h2, px, rev, rng, st){
  const w=(w2+px*0.16)*Math.min(1,rev*1.25), h=h2*1.28, r=h*0.5, rot=(rng()-.5)*0.06;
  ctx.save(); ctx.rotate(rot); ctx.fillStyle='rgba(232,33,31,0.30)';
  const yT=-h*0.55+(rng()-.5)*px*0.05, yB=h*0.5, x0=-w, x1=w;
  ctx.beginPath();
  ctx.moveTo(x0+r,yT); ctx.lineTo(x1-r,yT*0.9);
  ctx.quadraticCurveTo(x1+px*0.05,yT,x1,yT+r); ctx.lineTo(x1,yB-r);
  ctx.quadraticCurveTo(x1,yB,x1-r,yB); ctx.lineTo(x0+r,yB*0.95);
  ctx.quadraticCurveTo(x0-px*0.05,yB,x0,yB-r); ctx.lineTo(x0,yT+r);
  ctx.quadraticCurveTo(x0,yT,x0+r,yT); ctx.closePath(); ctx.fill();
  ctx.restore();
}

function init(st){
  st.s.font = st.tk.registerFamily('ForgeLinger', FONTS.arialBlack) || undefined;
  st.s.doodles = []; st.s.lastSpawn = 0; st.s.pop = 0;
  // prebuilt faint red halftone, fading in from the top-right corner
  const W = st.W, H = st.H, cc = st.tk.createCanvas(W, H), g = cc.getContext('2d');
  const gap = 15, rmax = 4.2, diag = Math.hypot(W, H);
  for (let y = 0; y < H; y += gap) for (let x = 0; x < W; x += gap) {
    const t = Math.max(0, 1 - (Math.hypot(x - W, y) / diag) * 1.6);
    if (t <= 0) continue;
    g.fillStyle = 'rgba(232,33,31,' + (0.5 * t).toFixed(3) + ')';
    g.beginPath(); g.arc(x, y, rmax * t, 0, 7); g.fill();
  }
  st.s.halftone = cc;
}

function spawnDoodle(st, burst){
  const d = st.s.doodles, rnd = st.rnd;
  if (d.length >= 10) d.shift();
  let cx, cy, tries = 0;
  do { cx = R(rnd, .06, .94) * st.W; cy = R(rnd, .08, .92) * st.H; tries++; }
  while (tries < 6 && cx > st.W * .18 && cx < st.W * .82 && cy > st.H * .34 && cy < st.H * .66);
  const gen = GENS[Math.floor(rnd() * GENS.length)];
  d.push({ strokes: gen(rnd), x: cx, y: cy, scale: R(rnd, 55, 150) * (burst ? 1.1 : 1), rot: R(rnd, -Math.PI, Math.PI), red: rnd() < 0.55, lw: R(rnd, 2, 3.8), born: st.NOW, life: R(rnd, 2800, 4600), seed: R(rnd, 0, 1000) });
}
function onBeat(st, power){ if (power > 1.0) spawnDoodle(st, true); st.s.pop = Math.min(1, st.s.pop + power * 0.4); }
function onCue(st){ st.s.pop = 1; }

function background(ctx, st){
  st.s.pop *= 0.86;
  if (st.overlayMode) {
    // faint scarlet halftone top-right keeps the brand red present over footage
    ctx.save(); ctx.globalAlpha = 0.16; ctx.drawImage(st.s.halftone, 0, 0); ctx.restore();
    const g = ctx.createLinearGradient(0, st.H * 0.5, 0, st.H);
    g.addColorStop(0, 'rgba(0,0,0,0)'); g.addColorStop(1, 'rgba(0,0,0,0.5)');
    ctx.fillStyle = g; ctx.fillRect(0, st.H * 0.5, st.W, st.H * 0.5);
  } else {
    ctx.save(); ctx.globalAlpha = 0.5; ctx.drawImage(st.s.halftone, 0, 0); ctx.restore();
  }
  // ambient doodles in music/photos only — talk stays clean & poetic
  if (st.reactive && st.NOW - st.s.lastSpawn > (st.s.doodles.length < 4 ? 700 : 1400)) { st.s.lastSpawn = st.NOW; spawnDoodle(st, false); }
  ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  for (let i = st.s.doodles.length - 1; i >= 0; i--) {
    const o = st.s.doodles[i], age = (st.NOW - o.born) / o.life;
    if (age >= 1) { st.s.doodles.splice(i, 1); continue; }
    const reveal = Math.min(1, age / 0.3);
    let alpha = age < 0.3 ? age / 0.3 : age > 0.82 ? (1 - age) / 0.18 : 1; alpha *= o.red ? 0.9 : 0.6;
    ctx.save(); ctx.translate(o.x, o.y); ctx.rotate(o.rot); ctx.scale(o.scale, o.scale); ctx.lineWidth = o.lw / o.scale;
    ctx.strokeStyle = o.red ? 'rgba(232,33,31,' + alpha.toFixed(3) + ')' : 'rgba(244,241,234,' + alpha.toFixed(3) + ')';
    let total = 0; o.strokes.forEach(s => total += s.length); let budget = Math.floor(reveal * total);
    const jit = () => Math.sin((st.NOW / 100) + o.seed) * 0.014;
    for (const s of o.strokes) { if (budget <= 1) break; const m = Math.min(s.length, budget); budget -= s.length; ctx.beginPath(); for (let j = 0; j < m; j++) { const px = s[j][0] + jit(), py = s[j][1] + jit(); j ? ctx.lineTo(px, py) : ctx.moveTo(px, py); } ctx.stroke(); }
    ctx.restore();
  }
}

function paintToken(ctx, o){
  const { T, px, scl, alpha, emph, st, ghost, p, tw } = o, tk = st.tk;
  ctx.save(); ctx.globalAlpha = alpha; ctx.translate(o.cx, o.cy); ctx.scale(scl, scl);
  ctx.font = tk.font(px, 900, st.s.font); ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  if (emph && !ghost) {
    // a different hand-drawn mark per word, sketched on as the word reveals
    const h = hash(T), rng = seeded(h), kind = h % (MARKS.length + 1); // +1 == marker swipe
    const w2 = (tw || ctx.measureText(T).width) / 2, h2 = px * 0.46;
    const rev = Math.min(1, (p == null ? 1 : p) * 1.15);
    const col = (h >> 5) % 4 === 0 ? 'rgba(244,241,234,0.95)' : palette.accent; // mostly red, sometimes white
    // per-word funk (hash-stable, so no flicker): a little tilt + varied stroke weight
    const rot = (rng() - 0.5) * 0.13, lw = px * (0.05 + rng() * 0.055);
    if (kind === MARKS.length) swipe(ctx, w2, h2, px, rev, rng, st); // behind the text
    // the word itself — off-white, dark stroke + soft shadow so it reads on video
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.6)'; ctx.shadowBlur = px * 0.14; ctx.shadowOffsetY = px * 0.03;
    ctx.lineJoin = 'round'; ctx.lineWidth = px * 0.14; ctx.strokeStyle = 'rgba(0,0,0,0.85)'; ctx.strokeText(T, 0, 0);
    ctx.shadowColor = 'transparent'; ctx.fillStyle = palette.fg; ctx.fillText(T, 0, 0);
    ctx.restore();
    if (kind < MARKS.length) {
      ctx.save(); ctx.rotate(rot);
      sketch(ctx, MARKS[kind](w2, h2, px, rng), rev, lw, col, h, st);
      // ~30% of the time, stack a second different mark for extra unpredictability
      if (rng() < 0.30) {
        const k2 = (kind + 1 + Math.floor(rng() * (MARKS.length - 1))) % MARKS.length;
        ctx.rotate((rng() - 0.5) * 0.1);
        const col2 = col === palette.accent ? 'rgba(244,241,234,0.9)' : palette.accent;
        sketch(ctx, MARKS[k2](w2, h2, px, rng), rev, lw * 0.8, col2, h ^ 0x9e3779b9, st);
      }
      ctx.restore();
    }
  } else {
    ctx.shadowColor = 'rgba(0,0,0,0.6)'; ctx.shadowBlur = px * 0.14; ctx.shadowOffsetY = px * 0.03;
    ctx.lineJoin = 'round'; ctx.lineWidth = px * 0.14; ctx.strokeStyle = 'rgba(0,0,0,0.85)'; ctx.strokeText(T, 0, 0);
    ctx.shadowColor = 'transparent';
    ctx.fillStyle = palette.fg; ctx.fillText(T, 0, 0);
  }
  ctx.restore();
}

function drawCue(ctx, st){
  const anchor = st.mode === 'talk' ? 0.74 : 'center';
  layoutCue(ctx, st, { sizes: { big: 150, mid: 120, small: 64 }, anchor, safeW: 0.9, reveal: 0.3, trail: 1, trans: 0.2, family: st.s.font, paintToken });
}

function overlay(ctx, st){
  const tk = st.tk;
  ctx.save(); ctx.globalAlpha = 0.05; ctx.globalCompositeOperation = 'overlay'; ctx.drawImage(tk.grain(), -Math.random() * 100, -Math.random() * 100); ctx.restore();
  ctx.drawImage(tk.vignette(st.overlayMode ? 0.5 : 0.7, 320), 0, 0);
  const wm = st.opts && st.opts.watermark;
  if (wm) { ctx.save(); ctx.globalAlpha = 0.85; ctx.fillStyle = palette.accent; ctx.font = tk.font(30, 800, st.s.font); ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(wm.toUpperCase(), st.CX, st.H - 70); ctx.restore(); }
  if (!st.overlayMode) { ctx.fillStyle = palette.accent; ctx.fillRect(0, st.H - 6, st.W * st.progress, 6); }
}

module.exports = { id: 'linger', label: 'Minimal · underline & brackets', palette, init, background, drawCue, overlay, onBeat, onCue, paintToken };
